import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Clock, XCircle, ClipboardList, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { submitNursingHomeClaimReply } from "@/lib/nursing-home-claim-reply";

interface OwnerNursingHomeAccessStateProps {
  variant: "none" | "pending" | "rejected" | "needs_info";
  centerName?: string;
  claimNotes?: string | null;
  /** When set with `needs_info`, claimant can submit a reply without returning to the dashboard. */
  needsInfoClaimId?: string | null;
  onNeedsInfoReplySubmitted?: () => void | Promise<void>;
}

export function OwnerNursingHomeAccessState({
  variant,
  centerName,
  claimNotes,
  needsInfoClaimId,
  onNeedsInfoReplySubmitted,
}: OwnerNursingHomeAccessStateProps) {
  const [replyDraft, setReplyDraft] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  const handleNeedsInfoSubmit = async () => {
    const reply = replyDraft.trim();
    if (!needsInfoClaimId) return;
    if (reply.length < 3) {
      toast.error("Please add a short response (at least a few characters).");
      return;
    }
    setReplySubmitting(true);
    try {
      await submitNursingHomeClaimReply(needsInfoClaimId, reply);
      toast.success("Thanks — your claim is back in our review queue.");
      setReplyDraft("");
      await onNeedsInfoReplySubmitted?.();
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : "Could not submit your reply.";
      toast.error(msg);
    } finally {
      setReplySubmitting(false);
    }
  };
  const title =
    variant === "pending"
      ? "Claim still in review"
      : variant === "rejected"
        ? "Claim not approved"
        : variant === "needs_info"
          ? "We need more information"
          : "Not authorized";

  const description =
    variant === "pending"
      ? "You can manage this profile once your claim is approved. You’ll still see progress on your owner dashboard."
      : variant === "rejected"
        ? "You don’t have access to manage this nursing home. If you believe this is an error, contact support."
        : variant === "needs_info"
          ? "Read the note from our team below. You can reply here or from your owner dashboard — once we have what we need, we’ll continue reviewing your claim."
          : "You don’t have permission to manage this nursing home. Only verified owners can access these tools.";

  const Icon =
    variant === "pending"
      ? Clock
      : variant === "rejected"
        ? XCircle
        : variant === "needs_info"
          ? ClipboardList
          : AlertCircle;

  return (
    <Layout>
      <div
        className={`container py-8 ${variant === "needs_info" && needsInfoClaimId ? "max-w-xl" : "max-w-lg"}`}
      >
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center text-center">
              <Icon
                className={`h-12 w-12 mb-4 ${
                  variant === "pending"
                    ? "text-amber-500"
                    : variant === "rejected"
                      ? "text-destructive"
                      : variant === "needs_info"
                        ? "text-orange-600"
                        : "text-red-500"
                }`}
              />
              <h2 className="text-2xl font-bold mb-2">{title}</h2>
              {centerName ? (
                <p className="text-muted-foreground mb-2">
                  <span className="font-medium text-foreground">{centerName}</span>
                </p>
              ) : null}
              <p className="text-muted-foreground mb-4">{description}</p>
              {(variant === "rejected" || variant === "needs_info") && claimNotes ? (
                <p className="text-sm bg-muted/50 rounded-md p-3 mb-4 w-full text-left whitespace-pre-wrap">
                  <span className="font-medium">Note from Grandpal: </span>
                  {claimNotes}
                </p>
              ) : null}
              {variant === "needs_info" && needsInfoClaimId ? (
                <div className="w-full text-left space-y-2 mb-4">
                  <Label htmlFor="owner-nh-needs-info-reply">Your response</Label>
                  <Textarea
                    id="owner-nh-needs-info-reply"
                    placeholder="Provide the information requested (e.g. registration details, letter of authority, or how we can reach you)."
                    rows={4}
                    disabled={replySubmitting}
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                  />
                  <Button
                    type="button"
                    disabled={replySubmitting}
                    className="w-full sm:w-auto bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                    onClick={handleNeedsInfoSubmit}
                  >
                    {replySubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      "Submit response & continue review"
                    )}
                  </Button>
                </div>
              ) : null}
              <Button asChild className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover">
                <Link to="/owner/dashboard">Back to owner dashboard</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
